import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { RecordingPlaybackTracker } from '@hotjar/webapp/recordings/recording-playback-tracker';
import { increment as statefulIncrementTracker } from '@hotjar/webapp/recordings/useStatefulPlaybackTracker';
import { convertFromQueryToNewUiFormat } from '@hotjar/global-filters';
import * as gettingStartedGuide from '@hotjar/webapp/domains/activation/getting-started-guide';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { CURRENT_USER_QUERY_KEY } from '@hotjar/user';
import { HotjarFilterOptionAll } from '../../../store/list/types';
import { selectPartialRecordingProps } from '../../../store/player/slices/recordingsListSlice/selectPartialRecordingProps';
import {
  RECORDING_INDEX_STORAGE_KEY,
  RECORDINGS_LIST_STORAGE_KEY,
  SEARCH_CONTEXT_STORAGE_KEY,
  WATCH_RECORDINGS_IDS_LIST_STORAGE_KEY,
} from '../../../store/player/slices/recordingsListSlice/constants';
import { getRecordingPath } from '../../../utils/getRecordingPath';

import type { Site } from '@hotjar/site';
import type { Store, JsonFilters, Recording } from '../../../store/list/types';

/**
 * Launch the recording player.
 */
export const showRecording = (store: Store, recording: Recording): void => {
  const {
    currentPage,
    itemsPerPage,
    recordings,
    recordingsRequestTimeRange,
    sortBy,
    site,
    jsonFilters,
    hotjarFilters,
  } = store.getState();

  const user: { id: number } | undefined = queryClient.getQueryData(CURRENT_USER_QUERY_KEY);
  const readableFilters = convertFromQueryToNewUiFormat(jsonFilters as JsonFilters);

  RecordingPlaybackTracker.incrementWatched();

  statefulIncrementTracker((site as Site).id, user?.id ?? 0);

  // Similarly, store the filters used in the current search.
  // This will allow us to "jump start" recordings to the first action which matches the filters.
  LocalStorage.set('recordingFiltersUiFormat', readableFilters);

  /**
   * Workaround for RECS-1047
   */
  if (hotjarFilters && hotjarFilters.watched !== HotjarFilterOptionAll) {
    LocalStorage.set(
      RECORDINGS_LIST_STORAGE_KEY,
      recordings.map((r) => selectPartialRecordingProps(r)),
    );
  } else {
    // Required for next / previous recordings in the player
    LocalStorage.set(SEARCH_CONTEXT_STORAGE_KEY, {
      recordingsRequestTimeRange,
      hotjarFilters,
      jsonFilters,
      sortBy,
    });
    LocalStorage.set(
      RECORDING_INDEX_STORAGE_KEY,
      (currentPage - 1) * itemsPerPage + recordings.findIndex(({ id }) => id === recording.id),
    );
  }

  // Clean up old localstorage as we changed it to "recordingFiltersUiFormat" with a new format
  // @todo This snippet can be removed after a reasonable amount of time has passed
  LocalStorage.remove('recordingFilters');

  const watchedRecordingsIdsList = recordings
    .filter((recording) => recording.watched)
    .map((recording) => recording.id);
  LocalStorage.set(WATCH_RECORDINGS_IDS_LIST_STORAGE_KEY, watchedRecordingsIdsList);

  window.open(getRecordingPath(recording, 'recordings-list'), '_blank');
  gettingStartedGuide.markRecordingAsSeen(site as Site, user?.id);

  // Show not watched by default on the next page load after a first recordings has been seen.
  if (!showNotWatchedByDefault()) {
    setShowNotWatchedByDefault();
  }
};

/**
 * Return true if we should show not watched recordings by default
 */
function showNotWatchedByDefault() {
  return !!LocalStorage.get('shouldShowNotWatchedByDefault');
}

/**
 * Set in local storage if the user should see not watched recordings by default
 */
function setShowNotWatchedByDefault() {
  LocalStorage.set('shouldShowNotWatchedByDefault', true);
}
